import { isToday, isTomorrow } from 'date-fns';
import type React from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoArrowBack } from 'react-icons/io5';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { isNil } from 'lodash-es';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useTimezone from '../../../hooks/useTimezone';
import type { ListAppointmentsResponseItemType } from '../../../proxies/AppointmentProxy';
import { getAppointmentDetailsRequest } from '../../../redux/ducks/AppointmentDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import Button from '../../button/Button';
import MainLayout from '../../layout/MainLayout/MainLayout';
import Spinner from '../../layout/Spinner/Spinner';

import { formatPartnerRouteName } from '../../../common/formatter';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import type { MarkerProps } from '../../layout/Map';
const CustomMap = lazyReactNaiveRetry(() => import('../../layout/Map'));

interface AppointmentDetailsProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const AppointmentDetails: React.FC<AppointmentDetailsProps> = ({
  ssrFetchedData,
}) => {
  const { navigate, toLocalizedRoute } = useAppNavigation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const { appointmentId } = useParams();
  const [appointment, setAppointment] = useState<
    ListAppointmentsResponseItemType | undefined
  >(undefined);

  const { getAppointmentDetailsResponse } = useSelector(
    (state: RootReducerType) => state.appointment,
  );

  const markers = useMemo<MarkerProps[]>(() => {
    if (!appointment) {
      return [];
    }

    return [
      {
        id: 'abcd',
        active: true,
        lat: appointment.address?.latitude || 0.0,
        lng: appointment.address?.longitude || 0.0,
      },
    ];
  }, [appointment]);

  const formatDay = (time: number): string => {
    const target = new Date(time);
    if (isToday(target)) {
      return t('static.generic.today');
    }

    if (isTomorrow(target)) {
      return t('static.generic.tomorrow');
    }

    return Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: currentTimezone,
    }).format(target);
  };

  const onChatClick = () => {
    if (appointment) {
      navigate(
        toLocalizedRoute(
          ROUTES.CONVERSATION_ROUTE.replace(
            ':conversationId',
            `${appointment.conversation}`,
          ),
        ),
      );
    }
  };

  const onNewAppointmentClick = () => {
    if (appointment) {
      const route = toLocalizedRoute(
        ROUTES.PARTNER_PUBLIC_PROFILE_ROUTE.replace(
          ':partnerId',
          `${appointment.partner.id}`,
        ).replace(
          ':partnerName',
          formatPartnerRouteName(
            `${appointment.partner.first_name} ${appointment.partner.last_name}`,
          ),
        ),
      );

      navigate(route);
    }
  };

  const onBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (getAppointmentDetailsResponse) {
      if (getAppointmentDetailsResponse?.data) {
        setAppointment(getAppointmentDetailsResponse?.data);
      }
    }
  }, [getAppointmentDetailsResponse]);

  useEffect(() => {
    if (!isNil(appointmentId)) {
      dispatch(getAppointmentDetailsRequest({ appointment: appointmentId }));
    }

    return () => {
      setAppointment(undefined);
    };
  }, []);

  return (
    <MainLayout
      categories={false}
      search={false}
      navbar
      footer
      ssrFetchedData={ssrFetchedData}
    >
      {appointment ? (
        <div className="flex h-full min-w-full flex-row">
          <div className="flex size-full flex-col p-2 sm:w-1/2 lg:w-1/3">
            <div className="flex h-full flex-col rounded-lg px-4 py-2 shadow">
              <button
                className="cursor-pointer py-6"
                id={`appointment-details-back-${appointment.id}`}
                key={`appointment-details-back-${appointment.id}`}
                aria-label={`appointment-details-back-${appointment.id}`}
                data-umami-event="appointment-details-back-selected"
                data-umami-event-appointment={`appointment-${appointment.id}`}
                data-test-id={`appointment-details-back-${appointment.id}`}
                type="button"
                onClick={onBackClick}
              >
                <IoArrowBack size={20} />
              </button>
              <div className="flex grow flex-col gap-6">
                <div className="text-2xl font-bold capitalize">
                  <h1>
                    {t('static.appointment_details.your_appointment_with', {
                      name: `${appointment.partner.first_name} ${appointment.partner.last_name}`,
                    })}
                  </h1>
                </div>
                {appointment.partner?.profile_picture && (
                  <div className="flex aspect-square min-w-full flex-col items-center justify-center">
                    <LazyLoadImage
                      alt={`${appointment.partner.first_name} ${appointment.partner.last_name}`}
                      style={{
                        objectFit: 'cover',
                        height: '100%',
                        width: '100%',
                      }}
                      src={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
                        appointment.partner.profile_picture
                      }`}
                      className="z-10 min-h-full min-w-full rounded-lg"
                      effect="blur"
                    />
                  </div>
                )}
                <div className="flex flex-col gap-5 text-lg font-semibold capitalize">
                  <div>
                    <div className="text-xl font-bold">
                      {appointment.business?.name
                        ? appointment.business?.name
                        : appointment.address.address}
                    </div>
                    {appointment.business?.name && (
                      <div>{appointment.address.address}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xl font-bold">
                      {formatDay(appointment.at)}
                    </div>
                    <div>
                      {appointment.service?.duration
                        ? `${Intl.DateTimeFormat(i18n.language, {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: false,
                            timeZone: currentTimezone,
                          }).format(
                            new Date(appointment.at),
                          )} - ${Intl.DateTimeFormat(i18n.language, {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: false,
                            timeZone: currentTimezone,
                          }).format(
                            new Date(
                              appointment.at +
                                appointment.service.duration * 1000,
                            ),
                          )}`
                        : Intl.DateTimeFormat(i18n.language, {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: false,
                            timeZone: currentTimezone,
                          }).format(new Date(appointment.at))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xl font-bold">
                      {appointment.service.name}
                    </div>
                    {appointment.service?.price &&
                      appointment.service?.currency && (
                        <div>
                          {new Intl.NumberFormat(i18n.language, {
                            style: 'currency',
                            currency: appointment.service.currency,
                          }).format(appointment.service.price)}
                        </div>
                      )}
                    {appointment.service?.duration && (
                      <div>
                        {t(
                          'static.appointment_details.service_duration_label',
                          {
                            duration: Math.floor(
                              appointment.service.duration / 60,
                            ),
                          },
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-10 flex flex-col gap-2">
                  <Button
                    disabled={false}
                    loading={false}
                    label={t(
                      'static.appointment_details.new_appointment_button_label',
                    )}
                    onClick={onNewAppointmentClick}
                    data-test-id="appointment-button-01"
                    id="appointment-button-01"
                  />
                  <Button
                    disabled={false}
                    loading={false}
                    label={t('static.appointment_details.go_to_messages_label')}
                    onClick={onChatClick}
                    data-test-id="appointment-button-02"
                    id="appointment-button-02"
                    outline
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="grow bg-yellow-500">
            <div className="size-full">
              <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                <CustomMap
                  id="appointment_view_map"
                  zoom={18}
                  center={[markers[0].lat, markers[0].lng]}
                  readOnly={false}
                  zoomControl={false}
                  scrollWheelZoom
                  markers={markers}
                />
              </Suspense>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-96 min-w-full flex-col items-center justify-center">
          <Spinner color="fill-sky-600" />
        </div>
      )}
    </MainLayout>
  );
};

export default AppointmentDetails;
